/**
 * Input types that summon the iOS keyboard. An allowlist rather than
 * `instanceof HTMLInputElement`: checkbox/radio/file/button-ish inputs
 * and readOnly fields (e.g. the copyable invite-link field) hold focus
 * WITHOUT a keyboard — treating those as "keyboard up" would chase
 * Chrome's toolbar-collapse offsets again, the exact stuck-shell bug the
 * focus gate exists to prevent. (Unknown `type` values normalize to
 * "text".)
 */
const KEYBOARD_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "url",
  "tel",
  "password",
  "number",
]);

/**
 * Can the on-screen keyboard plausibly be up right now?
 *
 * iOS only presents the keyboard for a focused editable, so "nothing
 * that summons a keyboard has focus" is a hard proof that no keyboard is
 * on screen — the invariant both `useIosViewportGuard` and
 * `useDetectKeyboardOpen` use to un-stick themselves when WebKit drops
 * the `visualViewport` resize that should have announced the dismissal.
 *
 * `<select>` is deliberately excluded: iOS 14+ presents it as a popover
 * menu that never resizes or pans the viewport.
 *
 * A focused `<iframe>` IS included — focus inside a cross-origin frame
 * (the BFN calculator) surfaces as the frame element, and its keyboard
 * shrinks our viewport exactly like our own inputs do. KNOWN RESIDUAL:
 * the frame keeps focus after its inner field blurs, and we cannot see
 * that, so an iframe is the one place a hold can outlast its keyboard.
 * Accepted deliberately — excluding iframes would drop the shell to full
 * height under the calculator's keyboard, which is the under-the-status-
 * bar bug this whole guard exists to prevent, and a hold that is merely
 * late still clears on the next resume, rotation or viewport event.
 */
export function isKeyboardSummoningFocus(): boolean {
  const el = document.activeElement;
  if (el instanceof HTMLInputElement) {
    return !el.readOnly && KEYBOARD_INPUT_TYPES.has(el.type);
  }
  if (el instanceof HTMLTextAreaElement) {
    return !el.readOnly;
  }
  if (el instanceof HTMLIFrameElement) {
    return true;
  }
  return el instanceof HTMLElement && Boolean(el.isContentEditable);
}
